import { Injectable, signal } from '@angular/core'

/**
 * User preferences stored in localStorage
 */
export interface UserPreferences {
  language: string
}

/**
 * Service to manage user preferences with localStorage persistence
 * Preferences are stored with a key prefix based on the application name
 */
@Injectable({
  providedIn: 'root',
})
export class PreferencesService {
  // Application name used as prefix for localStorage keys
  private readonly APP_NAME = 'sos-corp'

  // Default preferences
  private readonly DEFAULT_PREFERENCES: UserPreferences = {
    language: 'fr',
  }

  // Reactive signals for preferences
  language = signal<string>(this.DEFAULT_PREFERENCES.language)

  constructor() {
    this.loadPreferences()
  }

  /**
   * Load all preferences from localStorage on initialization
   */
  private loadPreferences(): void {
    const savedLanguage = this.getPreference('language')
    if (savedLanguage) {
      this.language.set(savedLanguage)
      console.log('✅ Preferences loaded:', { language: savedLanguage })
    } else {
      console.log('ℹ️ No saved preferences, using defaults')
    }
  }

  /**
   * Get a preference value from localStorage
   */
  private getPreference(key: keyof UserPreferences): string | null {
    const storageKey = this.buildStorageKey(key)
    return localStorage.getItem(storageKey)
  }

  /**
   * Set a preference value in localStorage
   */
  private setPreference(key: keyof UserPreferences, value: string): void {
    const storageKey = this.buildStorageKey(key)
    localStorage.setItem(storageKey, value)
    console.log(`💾 Preference saved: ${key} = ${value}`)
  }

  /**
   * Build the full localStorage key with app name prefix
   */
  private buildStorageKey(key: string): string {
    return `${this.APP_NAME}.${key}`
  }

  /**
   * Set the user's preferred language
   */
  setLanguage(language: string): void {
    this.language.set(language)
    this.setPreference('language', language)
  }

  /**
   * Get the current language preference
   */
  getLanguage(): string {
    return this.language()
  }

  /**
   * Clear all preferences from localStorage
   */
  clearPreferences(): void {
    const keys: Array<keyof UserPreferences> = ['language']
    keys.forEach((key) => {
      const storageKey = this.buildStorageKey(key)
      localStorage.removeItem(storageKey)
    })

    // Reset to defaults
    this.language.set(this.DEFAULT_PREFERENCES.language)
    console.log('🗑️ All preferences cleared')
  }

  /**
   * Get all current preferences as an object
   */
  getAllPreferences(): UserPreferences {
    return {
      language: this.language(),
    }
  }
}
